import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import * as api from "../../fileUploaderAdminApi";

export default function TemplatesList() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await api.fetchTemplates(projectId);
      setTemplates(data);
    } catch (err) {
      setError(err.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteTemplate(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message || "Failed to delete template.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {templates.length} template{templates.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/projects/${projectId}/file-uploader/admin/templates/new`)}
        >
          New Template
        </Button>
      </Box>

      {templates.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            py: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            color: "text.secondary",
          }}
        >
          <FolderOpenOutlinedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
          <Typography variant="h6" color="text.secondary">
            No templates yet
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={380}>
            Create a template manually or upload a sample file on the creation form to automatically detect columns.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/projects/${projectId}/file-uploader/admin/templates/new`)}
            sx={{ mt: 1 }}
          >
            New Template
          </Button>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell>Template Name</TableCell>
                <TableCell>Schema</TableCell>
                <TableCell>Accepted File Types</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((tmpl) => (
                <TableRow
                  key={tmpl.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/projects/${projectId}/file-uploader/admin/templates/${tmpl.id}/edit`)
                  }
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {tmpl.name}
                    </Typography>
                    {tmpl.description && (
                      <Typography variant="caption" color="text.secondary">
                        {tmpl.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{tmpl.schema_name || "—"}</Typography>
                    {tmpl.schema_name && (
                      <Typography variant="caption" color="text.secondary">
                        {tmpl.columns.length} column{tmpl.columns.length !== 1 ? "s" : ""}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {[...new Set((tmpl.file_type_rows ?? []).map((r) => r.extension))].map((ext) => (
                        <Chip
                          key={ext}
                          label={ext.replace(".", "").toUpperCase()}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: "6px" }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(tmpl.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/projects/${projectId}/file-uploader/admin/templates/${tmpl.id}/edit`
                          )
                        }
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(tmpl)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
